import type { PageResult } from './types';

/** 雪花 ID 等超出 JS 安全整数范围的字段，在 JSON 中保持字符串 */
const LONG_ID_FIELD_RE = /"(id|configId|knowledgeId|createBy|updateBy|uid|ossId|folderId)"\s*:\s*(\d{16,})/g;

export type ApiLongId = string | number;

/** 与 admin request.ts 一致，后端部分接口（含文件上传）依赖 clientId */
export function getApiClientId(): string | undefined {
  const id = import.meta.env.VITE_GLOB_APP_CLIENT_ID
    || import.meta.env.VITE_APP_CLIENT_ID;
  const trimmed = id?.trim();
  return trimmed || undefined;
}

/** 请求体/路径中的长整型 ID，统一以字符串提交，避免精度丢失 */
export function toApiLongId(value: ApiLongId | null | undefined): string | undefined {
  if (value == null || value === '')
    return undefined;
  return String(value);
}

/** 解析知识库相关 JSON 响应，将长整型 ID 字段保留为字符串 */
export function parseJsonPreserveLongIds(text: string): unknown {
  if (!text)
    return null;
  try {
    return JSON.parse(text.replace(LONG_ID_FIELD_RE, '"$1":"$2"'));
  }
  catch {
    return JSON.parse(text);
  }
}

/** 兼容 { rows, total } 与 { data: { rows, total } } 两种响应结构 */
export function unwrapPageResult<T = unknown>(res: any): { rows: T[]; total: number } {
  const rows = res?.rows ?? res?.data?.rows ?? [];
  const total = res?.total ?? res?.data?.total ?? rows.length;
  return { rows: (Array.isArray(rows) ? rows : []) as T[], total: Number(total) || 0 };
}

/** 兼容详情接口 { data } 与直接返回实体 */
export function unwrapData<T>(res: any): T {
  if (res?.data != null && typeof res.data === 'object' && !Array.isArray(res.data))
    return res.data as T;
  return res as T;
}

export function unwrapPageResponse<T>(res: any): PageResult<T> {
  const { rows, total } = unwrapPageResult<T>(res);
  return { rows, total };
}
